import React, { useState, useEffect, useMemo } from "react";
import { Search, Settings  } from "lucide-react";
import { getAllStudents } from "../../../services/StudentRecordService.js";
import { Checkbox } from "../../SharedInputs.jsx";

function LinkedStudentsList({ onCancel, initialData, onSelectStudent }) {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [activeTab, setActiveTab] = useState("existing");

  // Sorting + filters
  const [sortConfig, setSortConfig] = useState({});
  const [columnFilters, setColumnFilters] = useState({});
  const [showColumnFilters, setShowColumnFilters] = useState({});
  const [showColumnVisibility, setShowColumnVisibility] = useState(false);
  const brandColor = "#056ec4"; // Tailwind's blue-600

  // Visible columns
  const defaultColumns  = {
    studentId: true,
    firstName: true,
    lastName: true,
    campusName: true,
    agentName: true,
    enrolmentType: true,
    createdBy: true,
    createdAt: true,
    updatedAt: true,
    coeType: false,
    phone: false,
    usiNumber: false,
    visaApplicationType: false,
    activeCourse: false,
    courseStartDate: false,
    courseEndDate: false,
    coeIssuedDate: false,
    coeNumber: false,
    coeStatus: false,
    prismsDueDate: false,
    prismStatus: false,
  };
  
  // Load from sessionStorage if available
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = sessionStorage.getItem("studentVisibleColumns");
    return saved ? JSON.parse(saved) : defaultColumns;
  });
  const fetchStudents = async () => {
    setLoading(true);
    try {
        const data = await getAllStudents();
        const agentName = initialData?.agentName?.toLowerCase?.();
        const filteredStudents = (data || []).filter(
        (student) =>
            student?.formState?.agentName?.toLowerCase?.() === agentName
        );

        setStudents(filteredStudents);
    } catch (err) {
      console.error("Failed to fetch students:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStudents();
  }, []);

  const newStudents = students.filter(
    (s) => s?.formState?.studentStatus === "New Enrolment"
  );
  const existingStudents = students.filter(
    (s) => s?.formState?.studentStatus !== "New Enrolment"
  );

  let displayedStudents =
    activeTab === "new" ? newStudents : existingStudents;

  // Global search
  if (searchText) {
    const lower = searchText.toLowerCase();
    displayedStudents = displayedStudents.filter((s) =>
      Object.values(s?.formState || {}).some(
        (v) => v && v.toString().toLowerCase().includes(lower)
      )
    );
  }

  // Apply column filters
  const filteredStudents = displayedStudents.filter((s) => {
    const f = s?.formState || {};
    return Object.keys(columnFilters).every((key) => {
      const filterValue = columnFilters[key]?.toLowerCase?.() || "";
      if (!filterValue) return true;

      let value = "";
      
      // Special handling for Enrolment Type
      if (key === "enrolmentType") {
        value = f.enrolmentType || "COE"; // treat empty as "COE"
      }
      // Dates
      else if (key === "createdAt" || key === "updatedAt") {
        value = s[key]?.toDate?.().toLocaleDateString?.() || "";
      } 
      // Normal fields
      else {
        value = f[key] || "";
      }

      return value.toString().toLowerCase().includes(filterValue);
    });
  });

// Sorting logic per column
const sortedStudents = useMemo(() => {
  const today = new Date();

  // Only one active sort key at a time
  const sortKey = Object.keys(sortConfig).find((k) => sortConfig[k]);
  const direction = sortKey ? sortConfig[sortKey] : null;
  if (!sortKey || !direction) return filteredStudents;

  // Helper to get active or next upcoming course
  const getRelevantCourse = (student) => {
    const courses = student.formState?.courses || [];
    let course = courses.find((c) => {
      const start = c.courseStartDate ? new Date(c.courseStartDate) : null;
      const end = c.courseEndDate ? new Date(c.courseEndDate) : null;
      return start && end && today >= start && today <= end;
    });

    if (!course) {
      course = courses
        .filter((c) => c.courseStartDate)
        .map((c) => ({ ...c, start: new Date(c.courseStartDate) }))
        .filter((c) => c.start > today)
        .sort((a, b) => a.start - b.start)[0];
    }

    return course || {};
  };

  return [...filteredStudents].sort((a, b) => {
    let valA, valB;

    // Special date fields
    if (["createdAt", "updatedAt"].includes(sortKey)) {
      valA = a[sortKey]?.toDate?.().getTime?.() || 0;
      valB = b[sortKey]?.toDate?.().getTime?.() || 0;
    } 
    // Active course derived fields
    else if (
      [
        "activeCourse",
        "courseStartDate",
        "courseEndDate",
        "coeIssuedDate",
        "coeNumber",
        "coeStatus",
        "prismsDueDate",
        "prismStatus",
      ].includes(sortKey)
    ) {
      const courseA = getRelevantCourse(a);
      const courseB = getRelevantCourse(b);

      switch (sortKey) {
        case "activeCourse":
          valA = courseA.courseName?.toLowerCase() || "";
          valB = courseB.courseName?.toLowerCase() || "";
          break;
        case "courseStartDate":
          valA = courseA.courseStartDate ? new Date(courseA.courseStartDate).getTime() : 0;
          valB = courseB.courseStartDate ? new Date(courseB.courseStartDate).getTime() : 0;
          break;
        case "courseEndDate":
          valA = courseA.courseEndDate ? new Date(courseA.courseEndDate).getTime() : 0;
          valB = courseB.courseEndDate ? new Date(courseB.courseEndDate).getTime() : 0;
          break;
        case "coeIssuedDate":
          valA = courseA.coeIssuedDate ? new Date(courseA.coeIssuedDate).getTime() : 0;
          valB = courseB.coeIssuedDate ? new Date(courseB.coeIssuedDate).getTime() : 0;
          break;
        case "coeNumber":
          valA = courseA.coeNumber?.toLowerCase() || "";
          valB = courseB.coeNumber?.toLowerCase() || "";
          break;
        case "coeStatus":
          valA = courseA.coeStatus?.toLowerCase() || "";
          valB = courseB.coeStatus?.toLowerCase() || "";
          break;
        case "prismsDueDate":
          valA = courseA.prismsDueDate ? new Date(courseA.prismsDueDate).getTime() : 0;
          valB = courseB.prismsDueDate ? new Date(courseB.prismsDueDate).getTime() : 0;
          break;
        case "prismStatus":
          valA = courseA.prismStatus?.toLowerCase() || "";
          valB = courseB.prismStatus?.toLowerCase() || "";
          break;
      }
    } 
    // Regular fields from formState
    else {
      valA = (a.formState?.[sortKey] || "").toString().toLowerCase();
      valB = (b.formState?.[sortKey] || "").toString().toLowerCase();
    }

    if (valA < valB) return direction === "asc" ? -1 : 1;
    if (valA > valB) return direction === "asc" ? 1 : -1;
    return 0;
  });
}, [filteredStudents, sortConfig]);



  const handleSort = (key) => {
    // setSortConfig((prev) => {
    //   const newDirection =
    //     prev[key] === "asc" ? "desc" : prev[key] === "desc" ? null : "asc";
    //   return { ...prev, [key]: newDirection };
    // });
     setSortConfig((prev) => {
      // If the same column is clicked, toggle direction: asc -> desc -> none
      if (prev[key] === "asc") return { [key]: "desc" };
      if (prev[key] === "desc") return { [key]: null };
      // Otherwise, new column clicked, set to ascending and clear all others
      return { [key]: "asc" };
    });
  };

  const handleColumnFilterChange = (key, value) => {
    
    setColumnFilters((prev) => ({ ...prev, [key]: value }));
  };

  const toggleColumnFilter = (key) => {
    setShowColumnFilters((prev) => {
      const newState = { ...prev, [key]: !prev[key] };
      
      // If we are hiding the filter, clear its value
      if (!newState[key]) {
        setColumnFilters((filters) => ({ ...filters, [key]: "" }));
      }

      return newState;
    });
  };


  const columns = [
    { key: "studentId", label: "Student ID" },
    { key: "firstName", label: "First Name" },
    { key: "lastName", label: "Last Name" },
    { key: "campusName", label: "Campus" },
    { key: "agentName", label: "Agent" },
    {key: "enrolmentType", label: "Enrolment Type" },
    { key: "createdBy", label: "Created/Updated By" },
    { key: "createdAt", label: "Created On" },
    { key: "updatedAt", label: "Last Updated On" },
    { key: "coeType", label: "COE Type" },
    { key: "phone", label: "Phone" },
    { key: "usiNumber", label: "USI Number" },
    { key: "visaApplicationType", label: "Visa Application" },
    { key: "activeCourse", label: "Active/Upcoming Course" },
    { key: "courseStartDate", label: "Course Start Date" },
    { key: "courseEndDate", label: "Course End Date" },
    { key: "coeIssuedDate", label: "COE Issued Date" },
    { key: "coeNumber", label: "COE Number" },
    { key: "coeStatus", label: "COE Status" },
    { key: "prismsDueDate", label: "Prism Due Date" },
    { key: "prismStatus", label: "Prism Status" },
  ];
  const toggleColumn = (key) => {
    setVisibleColumns((prev) => {
      const updated = { ...prev, [key]: !prev[key] };
      sessionStorage.setItem("studentVisibleColumns", JSON.stringify(updated));
      return updated;
    });
  };


  return (
    <div className="record-list">
      

      {/* Tabs + Column Visibility */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        {/* Tabs */}
        <div style={{ display: "flex", gap: "0.5rem" }}>
          {[
            { key: "existing", label: `Existing Students (${existingStudents.length})` },
            { key: "new", label: `New Students (${newStudents.length})` },
          ].map((tab) => (
            <div
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                padding: "0.5rem 1.2rem",
                cursor: "pointer",
                borderRadius: "8px 8px 0 0",
                backgroundColor: activeTab === tab.key ? "#056ec4" : "#f0f0f0",
                color: activeTab === tab.key ? "#fff" : "#056ec4",
                fontWeight: activeTab === tab.key ? 600 : 500,
                boxShadow: activeTab === tab.key ? "0 2px 6px rgba(0,0,0,0.1)" : "none",
                transition: "all 0.2s ease",
              }}
            >
              {tab.label}
            </div>
          ))}
        </div>
          <div style={{display: "flex", justifyContent: "space-between", border: "1px solid #ddd",
                    borderRadius: "6px", marginBottom: "0.5rem"}}>
            {/* Search */}
            {/* Top controls: search + column visibility */}
            {activeTab === "existing" && (
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                {/* Global search */}
                <input
                  type="text"
                  placeholder="Search by ID, Name..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  className="form-input"
                  style={{ maxWidth: "300px", border: "none" }}
                />
              </div>
            )}
            {/* Column visibility */}
              <div style={{ position: "relative" }}>
                <button
                  onClick={() => setShowColumnVisibility((prev) => !prev)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    cursor: "pointer",
                    border:"none",
                    background: "#e9e9e9ff",
                    padding: "0.75rem",
                  }}
                >
                  <Settings size={18} /> Columns
                </button>

                {showColumnVisibility && (
                  <div
                    style={{
                      position: "absolute",
                      top: "110%",
                      right: 0,
                      background: "#fff",
                      border: "1px solid #ddd",
                      borderRadius: "6px",
                      padding: "8px",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                      zIndex: 100,
                      maxHeight: "300px",
                      overflowY: "auto",
                      minWidth: "200px",
                    }}
                  >
                    {columns.map((col) => (
                      <Checkbox
                        key={col.key}
                        checked={visibleColumns[col.key]}
                        onChange={() => toggleColumn(col.key)}
                      >
                        {col.label}
                      </Checkbox>
                    ))}
                  </div>
                )}
              </div>
          </div>
        
      </div>


      <div
        style={{
          maxWidth: "100%",
          position: "relative",          // keep it within layout
          maxHeight: "calc(100vh - 300px)", // adjust for header, tabs, search input
          overflowY: "auto",
          overflowX: "auto",
          border: "1px solid #ddd",
          borderRadius: "8px",
        }}
      >
        <table
          className="record-table"
          style={{borderCollapse: "collapse", tableLayout: "fixed" }}
        >
          <thead>
            {/* Header Row */}
            <tr style={{ borderBottom: "2px solid #ddd" }}>
              {columns.map(
                (col) =>
                  visibleColumns[col.key] && (
                    <th
                      key={col.key}
                      onClick={() => handleSort(col.key)}
                      style={{
                        padding: "8px",
                        textAlign: "center",
                        cursor: "pointer",
                        position: "sticky", 
                        top: 0,         
                        background: "#fff", 
                        zIndex: 10,  
                        whiteSpace: "nowrap",
                      }}
                    >
                      <span style={{ marginRight: "22px" }}>
                        {col.label}{" "}
                        {sortConfig[col.key] === "asc"
                          ? "▲"
                          : sortConfig[col.key] === "desc"
                          ? "▼"
                          : ""}
                      </span>

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleColumnFilter(col.key);
                        }}
                        style={{
                          position: "absolute",
                          right: "5px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          color: brandColor,
                          padding: "3px",
                        }}
                      >
                        <Search size={15} />
                      </button>
                    </th>
                  )
              )}
            </tr>

            {/* Filter Input Row */}
            <tr>
              {columns.map(
                (col) =>
                  visibleColumns[col.key] && (
                    <th
                      key={col.key}
                      style={{
                        padding: "4px",
                        textAlign: "center",
                        background: "#fafafa",
                      }}
                    >
                      {showColumnFilters[col.key] && (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                        }}
                      >
                        <input
                          type="text"
                          placeholder={`Search`}
                          value={columnFilters[col.key] || ""}
                          onChange={(e) => handleColumnFilterChange(col.key, e.target.value)}
                          style={{
                            width: "90%",
                            fontSize: "0.85rem",
                            padding: "4px",
                            textAlign: "center",
                          }}
                        />
                        {/* Only show count if there is some input */}
                        {columnFilters[col.key] && (
                          <span style={{ fontSize: "0.75rem", color: "#555", marginTop: "2px" }}>
                            {sortedStudents.length} student{sortedStudents.length !== 1 ? "s" : ""} found
                          </span>
                        )}
                      </div>
                    )}

                    </th>
                  )
              )}
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={columns.length}
                  style={{ textAlign: "center", padding: "1rem" }}
                >
                  Loading students...
                </td>
              </tr>
            ) : sortedStudents.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  style={{ textAlign: "center", color: "#777", padding: "1rem" }}
                >
                  No students found
                </td>
              </tr>
            ) : (
              sortedStudents.map((student) => {
                const f = student?.formState || {};
                // Find the active course based on current date
                const today = new Date();
                let activeCourseObj = (f.courses || []).find((course) => {
                  const start = course.courseStartDate ? new Date(course.courseStartDate) : null;
                  const end = course.courseEndDate ? new Date(course.courseEndDate) : null;
                  return start && end && today >= start && today <= end;
                });
                // If no active course, find the next upcoming course
                if (!activeCourseObj) {
                  activeCourseObj = (f.courses || [])
                    .filter((course) => course.courseStartDate)
                    .map((course) => ({ ...course, start: new Date(course.courseStartDate) }))
                    .filter((course) => course.start > today) // only future courses
                    .sort((a, b) => a.start - b.start)[0]; // earliest upcoming
                }
                return (
                  <tr
                    key={student.id}
                    onClick={() => onSelectStudent(student)}
                    style={{
                      cursor: "pointer",
                      borderBottom: "1px solid #eee",
                    }}
                  >
                    {visibleColumns.studentId && (
                      <td style={{ padding: "8px", textAlign: "center" }}>
                        {f.studentId}
                      </td>
                    )}
                    {visibleColumns.firstName && (
                      <td style={{ padding: "8px", textAlign: "center" }}>
                        {f.firstName}
                      </td>
                    )}
                    {visibleColumns.lastName && (
                      <td style={{ padding: "8px", textAlign: "center" }}>
                        {f.lastName}
                      </td>
                    )}
                    {visibleColumns.campusName && (
                      <td style={{ padding: "8px", textAlign: "center" }}>
                        {f.campusName}
                      </td>
                    )}
                    {visibleColumns.agentName && (
                      <td style={{ padding: "8px", textAlign: "center" }}>
                        {f.agentName}
                      </td>
                    )}
                    {visibleColumns.enrolmentType && (
                      <td style={{ padding: "8px", textAlign: "center" }}>{f.enrolmentType || "COE"}</td>
                    )}
                    {visibleColumns.createdBy && (
                      <td style={{ padding: "8px", textAlign: "center" }}>
                        {f.createdBy || "-"}
                      </td>
                    )}
                    {visibleColumns.createdAt && (
                      <td style={{ padding: "8px", textAlign: "center" }}>
                        {student.createdAt?.toDate
                          ? student.createdAt.toDate().toLocaleDateString()
                          : "-"}
                      </td>
                    )}
                    {visibleColumns.updatedAt && (
                      <td style={{ padding: "8px", textAlign: "center" }}>
                        {student.updatedAt?.toDate
                          ? student.updatedAt.toDate().toLocaleDateString()
                          : "-"}
                      </td>
                    )}
                    {visibleColumns.coeType && (
                      <td style={{ padding: "8px", textAlign: "center" }}>{f.coeType || "-"}</td>
                    )}
                    {visibleColumns.phone && (
                      <td style={{ padding: "8px", textAlign: "center" }}>{f.phone || "-"}</td>
                    )}
                    {visibleColumns.usiNumber && (
                      <td style={{ padding: "8px", textAlign: "center" }}>{f.usiNumber || "-"}</td>
                    )}
                    {visibleColumns.visaApplicationType && (
                      <td style={{ padding: "8px", textAlign: "center" }}>{f.visaApplicationType || "-"}</td>
                    )}
                    {visibleColumns.activeCourse && (
                      <td style={{ padding: "8px", textAlign: "center" }}>
                        {activeCourseObj?.courseName || "-"}
                      </td>
                    )}
                    {visibleColumns.courseStartDate && (
                      <td style={{ padding: "8px", textAlign: "center" }}>
                        {activeCourseObj?.courseStartDate
                          ? new Date(activeCourseObj.courseStartDate).toLocaleDateString()
                          : "-"}
                      </td>
                    )}
                    {visibleColumns.courseEndDate && (
                      <td style={{ padding: "8px", textAlign: "center" }}>
                        {activeCourseObj?.courseEndDate
                          ? new Date(activeCourseObj.courseEndDate).toLocaleDateString()
                          : "-"}
                      </td>
                    )}
                    {visibleColumns.coeIssuedDate && (
                      <td style={{ padding: "8px", textAlign: "center" }}>
                        {activeCourseObj?.coeIssuedDate
                          ? new Date(activeCourseObj.coeIssuedDate).toLocaleDateString()
                          : "-"}
                      </td>
                    )}
                    {visibleColumns.coeNumber && (
                      <td style={{ padding: "8px", textAlign: "center" }}>
                        {activeCourseObj?.coeNumber
                          ||"-"}
                      </td>
                    )}
                    {visibleColumns.coeStatus && (
                      <td style={{ padding: "8px", textAlign: "center" }}>
                        {activeCourseObj?.coeStatus
                          ||"-"}
                      </td>
                    )}
                    {visibleColumns.prismsDueDate && (
                      <td style={{ padding: "8px", textAlign: "center" }}>
                        {activeCourseObj?.prismsDueDate
                          ? new Date(activeCourseObj.prismsDueDate).toLocaleDateString()
                          : "-"}
                      </td>
                    )}
                    {visibleColumns.prismStatus && (
                      <td style={{ padding: "8px", textAlign: "center" }}>
                        {activeCourseObj?.prismStatus
                          || "-"}
                      </td>
                    )}

                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

    </div>
  );
}

export default LinkedStudentsList;
